/**
 * Shared room-label anchor policy.
 *
 * Both the web app's floor-plan / 3D label renderers and the
 * pipeline debug pages call `chooseRoomLabelAnchors(room)` to decide
 * where to draw each room's label. Putting the rule here keeps the
 * debug page and the user-facing views in lock-step — when the rule
 * changes (and it has) we don't drift between them.
 *
 * The rule:
 *   - no sections                              → one label at
 *                                                room.center (the
 *                                                polylabel point
 *                                                stamped by Step 8.10
 *                                                of CV polygon build)
 *   - single section close to room.center      → one label at
 *                                                room.center
 *   - single section far from room.center      → one label at
 *                                                section.center (the
 *                                                RoomPlan-tagged spot
 *                                                — usually a fixture
 *                                                anchor in an L-arm)
 *   - multiple sections                        → one label per section
 *                                                at section.center
 *
 * "Close" — both of:
 *   1. **Inscribed-circle**: `dist(section.center, room.center) ≤
 *      room.centerClearance` where `centerClearance` is the radius
 *      of the largest inscribed circle at the polylabel anchor
 *      (stamped by Step 8.10 / kept-original handling). Geometry-
 *      aware: asks "does the section sit in the polylabel's open
 *      area?". A section that fails this lives in a different
 *      pocket of the polygon — partition walls or a narrow neck
 *      cut it off from the polylabel's open area, so the label is
 *      really about that pocket only.
 *   2. **Bbox cap**: `dist ≤ 0.30 × bbox-diagonal` of the room's
 *      floor polygon. A room-size-relative absolute-distance cap.
 *      A section that fails this is too far away in absolute terms
 *      regardless of polygon geometry, which is itself a strong
 *      "this label belongs to a part of the room" signal.
 *
 * Either signal failing means "section labels only part of the
 * polygon" → use `section.center`. Both must pass to count as close
 * → use the polylabel `room.center`. Wall processing always
 * stamps `centerClearance` on every output room, so there is no
 * separate legacy-data fallback.
 *
 * Returns an array of `{ point, text? }` objects so callers can render
 * them uniformly. `point` is `{ x, z }`. Callers compute the display
 * text themselves (each one renders text differently — capitalized,
 * with debug ID, with section.name prefix, etc).
 */

const CLOSE_THRESHOLD_BBOX_FRACTION = 0.3;

function bboxDiagonal(polygon) {
  if (!polygon || polygon.length < 2) return 0;
  let minX = Infinity,
    maxX = -Infinity,
    minZ = Infinity,
    maxZ = -Infinity;
  for (const p of polygon) {
    const px = typeof p.x === 'number' ? p.x : p[0];
    const pz = typeof p.z === 'number' ? p.z : p[2];
    if (px < minX) minX = px;
    if (px > maxX) maxX = px;
    if (pz < minZ) minZ = pz;
    if (pz > maxZ) maxZ = pz;
  }
  const w = maxX - minX;
  const h = maxZ - minZ;
  return Math.hypot(w, h);
}

function asXZ(p) {
  if (!p) return null;
  const x = typeof p.x === 'number' ? p.x : Array.isArray(p) ? p[0] : null;
  const z = typeof p.z === 'number' ? p.z : Array.isArray(p) ? p[2] : null;
  if (typeof x !== 'number' || typeof z !== 'number') return null;
  return { x, z };
}

/**
 * Returns `true` when `section.center` is close enough to the
 * polylabel anchor (`room.center`) to count as the same open area
 * for label / name purposes. Both tests must pass:
 *   1. Inscribed-circle: `dist ≤ room.centerClearance` (no partition
 *      walls between the section and the polylabel's open area).
 *   2. Bbox cap: `dist ≤ CLOSE_THRESHOLD_BBOX_FRACTION × bbox-
 *      diagonal` of the supplied polygon (not too far away in
 *      absolute terms).
 *
 * Either failing → the section labels only part of the polygon →
 * caller should anchor on `section.center`. Used by
 * `chooseRoomLabelAnchors` here and by Step 8.12
 * (`reattributeSectionsAndNames`) so the label-anchor policy and the
 * room-rename policy stay aligned.
 */
function isSectionCloseToCenter(room, section, polygon) {
  const roomCenter = asXZ(room?.center);
  const secCenter = asXZ(section?.center);
  if (!roomCenter || !secCenter) return false;
  const distance = Math.hypot(
    secCenter.x - roomCenter.x,
    secCenter.z - roomCenter.z
  );
  const inscribedClose = distance <= room.centerClearance;
  const poly = polygon || room?.floors?.[0]?.polygonCorners;
  const bboxClose =
    distance <= bboxDiagonal(poly) * CLOSE_THRESHOLD_BBOX_FRACTION;
  return inscribedClose && bboxClose;
}

/**
 * Returns an array of anchor objects for the room: one entry per
 * label the renderer should draw. Each entry is
 * `{ point: { x, z }, section: section|null }`. `section` is `null`
 * when the entry should use the room-level label, and the section
 * object when it's tied to a specific section (multi-section rooms).
 */
function chooseRoomLabelAnchors(room) {
  const roomCenter = asXZ(room?.center);
  const sections = Array.isArray(room?.sections) ? room.sections : [];

  if (sections.length === 0) {
    return roomCenter ? [{ point: roomCenter, section: null }] : [];
  }

  if (sections.length > 1) {
    const out = [];
    for (const sec of sections) {
      const c = asXZ(sec?.center);
      if (c) out.push({ point: c, section: sec });
    }
    return out;
  }

  // Single section: pick polylabel vs section.center by distance.
  const sec = sections[0];
  const secCenter = asXZ(sec?.center);
  if (!roomCenter && !secCenter) return [];
  if (!roomCenter) return [{ point: secCenter, section: sec }];
  if (!secCenter) return [{ point: roomCenter, section: sec }];

  const point = isSectionCloseToCenter(room, sec) ? roomCenter : secCenter;
  return [{ point, section: sec }];
}

module.exports = {
  chooseRoomLabelAnchors,
  isSectionCloseToCenter,
  CLOSE_THRESHOLD_BBOX_FRACTION,
};
